import { parseISO } from 'date-fns'
import {
  array,
  boolean,
  enum_,
  isoDate,
  number,
  object,
  optional,
  picklist,
  pipe,
  string,
  literal,
  union,
  type InferOutput
} from 'valibot'

import {
  createSorterSchema,
  isClosingDateBasis,
  SEARCH_DATE_BASIS,
  TOURS_SORT_KEYS
} from '@/features/shared/models'
import {
  CHARGE_STATUS_TYPE,
  CONTRACT_TYPE_WITH_ERROR_SELECTABLE,
  SORT_ORDER_TYPE
} from '@/libs/api/types'

import { isOperatingDateRangeWithinMaxMonths } from './operating-date-range'

// isoDate()もしくは空文字を許容
const isoDateOrEmpty = union([pipe(string(), isoDate()), literal('')])

/**
 * 運行実績の検索条件
 */
export const toursSearchSchema = object({
  page: optional(number()),
  take: optional(number()),
  ...createSorterSchema(TOURS_SORT_KEYS),
  order: optional(enum_(SORT_ORDER_TYPE)),
  /**
   * 運行実績ステータス（いずれかに一致）。画面のステータス名は見る立場ごとに複数の内部値を
   * まとめて出すため、選んだ名前にあたる内部値をすべて持つ（#6011）
   */
  statuses: optional(array(enum_(CHARGE_STATUS_TYPE))),
  /** 差し戻しの有無 */
  isRejected: optional(boolean()),
  /** 日報番号 */
  dailyReportNo: optional(string()),
  /** 運行日 From */
  operatingDateFrom: optional(isoDateOrEmpty),
  /** 運行日 To */
  operatingDateTo: optional(isoDateOrEmpty),
  /** 検索日付基準（省略時 DEFAULT = 既存の運行日検索。VOC_PJ-939 #4387） */
  searchDateBasis: optional(enum_(SEARCH_DATE_BASIS)),
  /** 計上月（yyyy-MM-dd に正規化。締日基準検索時のみ送る） */
  operatingYearMonth: optional(isoDateOrEmpty),
  /** 契約名 */
  contractName: optional(string()),
  /** 契約種別（個建は #5551 で入口を閉じたため、URL 直指定の TANKA も受け付けない） */
  contractType: optional(picklist(CONTRACT_TYPE_WITH_ERROR_SELECTABLE)),
  /** 荷主ID */
  customerIds: optional(array(number())),
  /** パートナーID */
  partnerIds: optional(array(number())),
  /** 拠点(物流センター)ID */
  dcId: optional(number()),
  /** 修正の有無  */
  hasUpdated: optional(boolean()),
  /** 車両番号 */
  truckCode: optional(string()),
  /** 超過時間割増の有無  */
  hasOvertime: optional(boolean()),
  /** 深夜時間割増の有無  */
  hasNightTime: optional(boolean()),
  /** 超過距離割増の有無  */
  hasExcessDistance: optional(boolean()),
  /** 追加業務の有無  */
  hasAddSite: optional(boolean()),
  /** 手動追加フラグ（true: 手動追加） */
  isManualTour: optional(boolean()),
  /** キャンセル運行を表示するかどうか */
  showCancelledTours: optional(boolean()),
  /** カスタムキー横断フリーワード検索（請求明細の customKey1〜5 のいずれかに部分一致。VOC_PJ-960 #4487） */
  customKeyFreeWord: optional(string())
})

export type ToursSearch = InferOutput<typeof toursSearchSchema>

/**
 * パートナーユーザーには出さない検索条件が URL にあるか（#5945）
 *
 * パートナーユーザーには荷主と請求の情報を出さないので、検索欄の荷主・検索基準の請求月・
 * 荷主の列（荷主名の並べ替え）を画面に出していない。ブックマークや共有リンクの URL に
 * これらが残っていると、画面に無い条件で検索することになる（API は請求月を 400 で弾き、
 * 荷主の絞り込みと荷主名の並べ替えは無視する）。
 */
export const hasPartnerHiddenConditions = (search: ToursSearch): boolean =>
  search.customerIds !== undefined ||
  search.searchDateBasis === SEARCH_DATE_BASIS.INCOME_CLOSING_DATE ||
  search.sort === 'customerName'

/**
 * パートナーユーザーには出さない検索条件を外す（#5945）
 *
 * 請求月は計上月ごと外して通常の検索に戻す（運行日は loader が既定レンジを補う）。
 * 荷主名の並べ替えは並び順ごと外して既定の並びに戻す。
 */
export const withoutPartnerHiddenConditions = (
  search: ToursSearch
): ToursSearch => ({
  ...search,
  customerIds: undefined,
  ...(search.searchDateBasis === SEARCH_DATE_BASIS.INCOME_CLOSING_DATE
    ? { searchDateBasis: undefined, operatingYearMonth: undefined }
    : {}),
  ...(search.sort === 'customerName'
    ? { sort: undefined, order: undefined }
    : {})
})

/**
 * 運行日のキーが必ず埋まっている検索条件（#4557）
 *
 * URL のクエリは運行日を持たないことがあるためスキーマ上は optional のままだが、
 * API は通常検索では運行日 From/To を必須にしたため、実際に API へ投げる検索条件は
 * loader が既定レンジを補ってこの型にする。
 *
 * 締日基準検索（#4387）だけは計上月が母集合を絞るので運行日が任意で、その場合は
 * 空文字（= レンジ指定なし）が入る。
 */
export type ToursSearchWithOperatingDate = ToursSearch & {
  operatingDateFrom: string
  operatingDateTo: string
}

/**
 * URL 由来の検索条件に既定の運行日レンジを補う（#4557）
 *
 * 運行日が未指定・空文字のときに既定レンジへ戻すことで、URL を直接編集して
 * 運行日を空にしても全期間検索にならないようにする。運行日以外の条件は URL 優先。
 *
 * From/To は API 上ペアで必須かつ期間上限があるため、**片方だけ欠けている場合も
 * ペアごと既定レンジに戻す**。片側だけ残すと、例えば
 * `?operatingDateFrom=2020-01-01&operatingDateTo=` が既定の To と組み合わさって
 * 期間上限を超え、初回取得が 400 になる。
 *
 * 両方が指定されていても**期間上限を超えていれば既定レンジに戻す**。運行日必須化より
 * 前にブックマークや共有リンクとして残った URL（例
 * `?operatingDateFrom=2020-01-01&operatingDateTo=2026-07-30`）は上限を超えるため、
 * そのまま流すと初回取得が 400 になる。一覧はエラー時に何も描画しないので、利用者は
 * 検索フォームすら出ない空の画面から条件を直せなくなる。
 */
export const withDefaultOperatingDateRange = (
  search: ToursSearch,
  defaultSearch: ToursSearchWithOperatingDate
): ToursSearchWithOperatingDate => {
  const { operatingDateFrom, operatingDateTo } = search

  // 締日基準検索（#4387）では計上月が母集合を絞るため、運行日は任意併用（Q-8 = 8-A）。
  // ここで既定レンジを補うと「請求計上月 2026-06 の全件」のつもりの検索が直近 7 日で
  // AND 絞りされ、利用者が指定していない条件で件数が落ちる。レンジ未指定は
  // 未指定のまま通し、両方指定されている場合だけ下の上限チェックに載せる。
  //
  // これは共有ヘルパー applyDefaultDateRange（VOC_PJ-990 #4809）と同じ判断で、
  // 運行実績一覧ではこの関数が代わりに担う。本画面は #4557 の期間上限クランプも
  // 同時に決める必要があり、判定を共有ヘルパーと分けると URL とフォームの値が
  // 食い違うため。判定条件（isClosingDateBasis）は共有のものを使う。
  const hasUrlRange = Boolean(operatingDateFrom) && Boolean(operatingDateTo)
  if (isClosingDateBasis(search.searchDateBasis) && !hasUrlRange) {
    return {
      ...defaultSearch,
      ...search,
      operatingDateFrom: '',
      operatingDateTo: ''
    }
  }

  const canUseUrlRange =
    Boolean(operatingDateFrom) &&
    Boolean(operatingDateTo) &&
    isOperatingDateRangeWithinMaxMonths(
      parseISO(operatingDateFrom as string),
      parseISO(operatingDateTo as string)
    )

  return {
    ...defaultSearch,
    ...search,
    operatingDateFrom:
      canUseUrlRange && operatingDateFrom
        ? operatingDateFrom
        : defaultSearch.operatingDateFrom,
    operatingDateTo:
      canUseUrlRange && operatingDateTo
        ? operatingDateTo
        : defaultSearch.operatingDateTo
  }
}
